/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { getAllRecords } from "./features/inspectionRecordSlice";
import Button from "components/base/Button";
import FeatherIcon from 'feather-icons-react';
import { IInspectionRecord } from "../model/inspection-record.model";
import { useNavigate } from "react-router-dom";
import { AnomalyRecord } from "data/entities/AnomalyRecord";
import { useTranslation } from "react-i18next";
import { getDateLocaleConfig } from "i18n/locale";

export default function InspectionRecordsTable() {
    const { t, i18n } = useTranslation();
    const tt = (key: string) => t(key, { ns: "translation" });
    const dispatch: any = useDispatch();
    const [selectedInspectionRecord, setSelectedInspectionRecord] = useState<any>(null);
    const navigate = useNavigate();
    const { allRecords, loading } = useSelector(
        (state: RootStateOrAny) => state.inspectionRecord
    );

    const [globalFilter, setGlobalFilter] = useState("");
    const dateLocale = getDateLocaleConfig(i18n.language);
    const dateTimeFormatter = React.useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale.intlLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }),
        [dateLocale.intlLocale]
    );

    useEffect(() => {
        dispatch(getAllRecords());
    }, [dispatch]);


    const dateTemplate = (rowData: any, field: string) => {
        const value = rowData[field];
        if (!value) return tt("pages.thermography.inspectionRecordsTable.notAvailable");
        const parsed = dayjs(value);
        if (!parsed.isValid()) return tt("pages.thermography.inspectionRecordsTable.notAvailable");
        return dateTimeFormatter.format(parsed.toDate());
    };

    const plantTemplate = (rowData: any) =>
        rowData.plant?.name ?? tt("pages.thermography.inspectionRecordsTable.notAvailable");

    const editRecord = (record: IInspectionRecord) => {
        navigate(`/inspectionRecords/${record.id}`);
    };

    const goToReport = (rowData: AnomalyRecord) => {
        navigate(`/inspectionRecords/report/${rowData.id}`);
    };

    // const deleteRecord = (route: IInspectionRecord) => {

    // };

    const actionBodyTemplate = (rowData: any) => {
        const isSelected = selectedInspectionRecord?.id === rowData.id;

        return (
            <div
                className={`flex gap-2 action-buttons ${isSelected ? 'selected' : ''}`}
            >
                <Button
                    onClick={() => goToReport(rowData)}
                    className={`icon-button pdf-button ${isSelected ? "selected" : ""}`}
                    aria-label={tt("pages.thermography.inspectionRecordsTable.actions.viewReportAria")}
                    title={tt("pages.thermography.inspectionRecordsTable.actions.viewPdfTitle")}
                >
                    <FeatherIcon icon="file-text" size={16} />
                </Button>

                <Button
                    onClick={() => editRecord(rowData)}
                    className={`icon-button edit-button ${isSelected ? 'selected' : ''}`}
                    aria-label={tt("pages.thermography.inspectionRecordsTable.actions.editAria")}
                    title={tt("pages.thermography.inspectionRecordsTable.actions.editTitle")}
                >
                    <FeatherIcon icon="edit-2" size={16} />
                </Button>

                {/* <Button
                    onClick={() => deleteRecord(rowData)}
                    className={`icon-button delete-button ${isSelected ? 'selected' : ''}`}
                    aria-label="Deletar"
                >
                    <FeatherIcon icon="trash-2" size={16} />
                </Button> */}
            </div>
        );
    };

    const nameTemplate = (rowData: any) => (
        <a
            href={`/inspectionRecords/view/${rowData.id}`}
            style={{ color: "blue", textDecoration: "underline" }}
        >
            {rowData.name}
        </a>
    );

    return (
        <div className="card">
            <DataTable
                key={`inspection-records-table-${i18n.language}`}
                value={allRecords}
                loading={loading}
                onRowClick={(e) => setSelectedInspectionRecord(e.data)}
                className="custom-datatable"
                size="small"
                dataKey="id"
                removableSort
                scrollable
                scrollHeight="600px"
                tableStyle={{ minWidth: '50rem' }}
                globalFilter={globalFilter}
                globalFilterFields={[
                    "code",
                    "name",
                    "description",
                    "plant.name",
                ]}
                emptyMessage={tt("pages.thermography.inspectionRecordsTable.emptyMessage")}
            >
                <Column
                    sortable
                    field="name"
                    header={tt("pages.thermography.inspectionRecordsTable.table.inspectionRecord")}
                    body={nameTemplate}
                />

                <Column
                    sortable
                    field="plant"
                    header={tt("pages.thermography.inspectionRecordsTable.table.installation")}
                    body={plantTemplate}
                />


                <Column
                    sortable
                    field="expectedStartDate"
                    header={tt("pages.thermography.inspectionRecordsTable.table.startDate")}
                    body={(row) => dateTemplate(row, "expectedStartDate")}
                />

                <Column
                    sortable
                    field="expectedEndDate"
                    header={tt("pages.thermography.inspectionRecordsTable.table.deadlineDate")}
                    body={(row) => dateTemplate(row, "expectedEndDate")}
                />

                <Column
                    sortable
                    field="finishedAt"
                    header={tt("pages.thermography.inspectionRecordsTable.table.closingDate")}
                    body={(row) => dateTemplate(row, "finishedAt")}
                />

                <Column sortable field="maintenanceDocument" header={tt("pages.thermography.inspectionRecordsTable.table.maintenanceDocument")} />

                <Column sortable field="createdBy.name" header={tt("pages.thermography.inspectionRecordsTable.table.thermographer")} />


                <Column
                    body={actionBodyTemplate}
                    header={tt("pages.thermography.inspectionRecordsTable.table.actions")}
                    style={{ width: "120px" }}
                />
            </DataTable>
        </div>
    );
}
